import axios from 'axios';

// API Configuration
const API_BASE = (() => {
  const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
  const localUrl = 'http://localhost:5001';
  
  // Check if we're forcing production API
  if (process.env.REACT_APP_USE_PROD_API === 'true') {
    console.log('🌐 Using production API endpoint (forced):', prodUrl);
    return prodUrl;
  }
  
  // Check if we're in production build
  if (process.env.NODE_ENV === 'production') {
    console.log('🌐 Using production API endpoint:', prodUrl);
    return prodUrl;
  }
  
  // Default to local for development
  console.log('🔧 Using local API endpoint:', localUrl);
  return localUrl;
})();

const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types for the new simplified structure
export interface User {
  userId: string;
  email: string;
  phoneNumber: string | null;
  fullName: string;
  clerkUserId: string | null;
  status: 'active' | 'inactive';
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  subscriptionId: string;
  userId: string;
  email: string;
  planId: string;
  status: 'active' | 'cancelled' | 'expired' | 'past_due';
  startDate: string;
  endDate: string;
  stripeSubscriptionId: string | null;
  stripePaymentIntentId: string | null;
  priceId: string | null;
  amount: number;
  currency: string;
  billingCycle: 'monthly' | 'yearly';
  autoRenew: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Plan {
  planId: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  stripePriceId: string;
  isActive: boolean;
  createdAt: string;
}

export interface UserWithSubscription {
  user: User;
  subscription: Subscription | null;
}

export interface SubscriptionStatusResponse {
  success: boolean;
  isSubscribed: boolean;
  user?: {
    userId: string;
    fullName: string;
    email: string;
  };
  subscription?: Subscription;
  message?: string;
}

// Helper function to generate userId from email (consistent with backend)
export const generateUserIdFromEmail = (email: string): string => {
  return email.toLowerCase().replace('@', '_at_').replace(/\./g, '_dot_');
};

// Helper function to format phone number
export const formatPhoneNumber = (phone: string): string => {
  return phone.startsWith('+') ? phone : `+${phone}`;
};



// ========================
// USER MANAGEMENT APIS
// ========================

/**
 * Create a new user (replaces createUserProfile)
 */
export const createUser = async (userData: {
  email: string;
  phoneNumber?: string;
  fullName: string;
  clerkUserId?: string;
  paymentMethods?: any; // Allow payment methods to be passed
  // Trial subscription parameters
  createTrialSubscription?: boolean;
  planId?: string;
  stripeCustomerId?: string;
  stripePaymentMethodId?: string;
  cardDetails?: any;
}): Promise<{ success: boolean; user?: User; id?: string; message: string; data?: any; subscription?: any }> => {
  try {
    console.log('👤 Creating user with new v2 API:', userData.email);
    console.log('🔗 API Base URL:', apiClient.defaults.baseURL);

    // Prepare payload, including payment methods if they exist
    const payload: any = {
      email: userData.email.toLowerCase(),
      phoneNumber: userData.phoneNumber ? formatPhoneNumber(userData.phoneNumber) : null,
      fullName: userData.fullName,
      clerkUserId: userData.clerkUserId
    };

    if (userData.paymentMethods) {
      payload.paymentMethods = userData.paymentMethods;
      console.log('💳 Including payment methods in user creation payload');
    }

    // Include trial subscription parameters if provided
    if (userData.createTrialSubscription) {
      payload.createTrialSubscription = true;
      payload.planId = userData.planId;
      payload.stripeCustomerId = userData.stripeCustomerId;
      payload.stripePaymentMethodId = userData.stripePaymentMethodId;
      payload.cardDetails = userData.cardDetails;
      console.log('🆓 Including trial subscription parameters in user creation payload');
    }

    console.log('📝 Request payload:', payload);

    // Use the new consolidated endpoint that handles both user creation and trial subscription
    const response = await apiClient.post('/users', payload);

    console.log('✅ User created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating user - Full error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      timeout: error.config?.timeout
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 409) {
      throw new Error('User already exists');
    } else if (error.response?.status === 404) {
      throw new Error('API endpoint not found. Please ensure Firebase Functions are running.');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      console.error('❌ Network error - no response received:', error.request);
      throw new Error('Network error: Cannot reach Firebase Functions. Please check if they are running.');
    } else {
      throw new Error('Failed to create user. Please try again.');
    }
  }
};

/**
 * Get user with subscription data (replaces getUserProfileData + subscription lookup)
 */
export const getUserWithSubscription = async (userId: string): Promise<UserWithSubscription> => {
  try {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error getting user with subscription:', error);
    
    if (error.response?.status === 404) {
      throw new Error('User not found');
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to get user data. Please try again.');
    }
  }
};

/**
 * Update user profile
 */
export const updateUser = async (userId: string, updates: {
  phoneNumber?: string;
  fullName?: string;
  status?: 'active' | 'inactive';
}): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.put(`/users/${userId}`, {
      ...updates,
      phoneNumber: updates.phoneNumber ? formatPhoneNumber(updates.phoneNumber) : undefined
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Error updating user:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to update user. Please try again.');
    }
  }
};

// ========================
// SUBSCRIPTION MANAGEMENT APIS
// ========================

/**
 * Create subscription for user (replaces addPaidUserData)
 */
export const createSubscription = async (subscriptionData: {
  email: string;
  phoneNumber: string;
  fullName: string;
  planId: string;
  stripePaymentIntentId?: string;
  stripeCustomerId?: string; // Added to support direct customer ID passing
  amount?: number;
}): Promise<{ success: boolean; subscription: Subscription; message: string }> => {
  try {
    // Generate userId from email to use in the new API endpoint
    const userId = generateUserIdFromEmail(subscriptionData.email);
    console.log('📧 Creating subscription for userId:', userId, 'with email:', subscriptionData.email);
    console.log('🔗 API Base URL:', apiClient.defaults.baseURL);
    console.log('📝 Subscription payload:', {
      planId: subscriptionData.planId,
      stripePaymentIntentId: subscriptionData.stripePaymentIntentId,
      stripeCustomerId: subscriptionData.stripeCustomerId,
      amount: subscriptionData.amount,
      billingCycle: 'monthly'
    });
    
    // Use the legacy endpoint that works: POST /user/paid-user-data  
    const response = await apiClient.post('/user/paid-user-data', {
      email: subscriptionData.email,
      phoneNumber: subscriptionData.phoneNumber,
      fullName: subscriptionData.fullName,
      planId: subscriptionData.planId,
      stripePaymentIntentId: subscriptionData.stripePaymentIntentId,
      stripeCustomerId: subscriptionData.stripeCustomerId, // Include customer ID in the API call
      amount: subscriptionData.amount,
      billingCycle: 'monthly'
    });
    
    if (subscriptionData.stripeCustomerId) {
      console.log('✅ Included customer ID in subscription creation:', subscriptionData.stripeCustomerId);
    } else {
      console.log('⚠️ No customer ID provided for subscription creation');
    }
    
    console.log('✅ Subscription created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating subscription - Full error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
      userId: generateUserIdFromEmail(subscriptionData.email),
      email: subscriptionData.email
    });
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('User not found - the user must be created before creating a subscription');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      console.error('❌ Network error - no response received:', error.request);
      throw new Error('Network error: Cannot reach Firebase Functions. Please check if they are running.');
    } else {
      throw new Error('Failed to create subscription. Please try again.');
    }
  }
};

/**
 * Get user's active subscription
 */
export const getUserSubscription = async (userId: string): Promise<{ 
  success: boolean; 
  subscription: Subscription | null; 
  plan?: Plan;
  message?: string;
}> => {
  try {
    const response = await apiClient.get(`/users/${userId}/subscription`);
    return response.data;
  } catch (error: any) {
    console.error('Error getting user subscription:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to get subscription data. Please try again.');
    }
  }
};

/**
 * Cancel subscription (marks as cancelled)
 */
export const cancelSubscription = async (subscriptionId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.delete(`/subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error cancelling subscription:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to cancel subscription. Please try again.');
    }
  }
};

/**
 * Permanently delete subscription (hard delete)
 */
export const deleteSubscription = async (subscriptionId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.delete(`/subscriptions/${subscriptionId}/delete`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting subscription:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to delete subscription. Please try again.');
    }
  }
};

/**
 * Check subscription status by phone number (for SMS validation)
 */
export const checkSubscriptionByPhone = async (phoneNumber: string): Promise<SubscriptionStatusResponse> => {
  try {
    const cleanPhone = formatPhoneNumber(phoneNumber);
    const response = await apiClient.get(`/users/phone/${encodeURIComponent(cleanPhone)}/subscription-status`);
    return response.data;
  } catch (error: any) {
    console.error('Error checking subscription by phone:', error);
    
    // For this endpoint, we want to return false rather than throw
    return {
      success: false,
      isSubscribed: false,
      message: 'Failed to check subscription status'
    };
  }
};

// ========================
// PLANS MANAGEMENT
// ========================

/**
 * Get available subscription plans
 */
export const getPlans = async (): Promise<{ success: boolean; plans: Plan[] }> => {
  try {
    const response = await apiClient.get('/plans');
    return response.data;
  } catch (error: any) {
    console.error('Error getting plans:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Please check your connection and try again.');
    } else {
      throw new Error('Failed to get plans. Please try again.');
    }
  }
};

// ========================
// CONVENIENCE FUNCTIONS
// ========================

/**
 * Complete user onboarding (creates user + subscription in one flow)
 * This replaces the old createUserProfile + addPaidUserData pattern
 */
export const completeUserOnboarding = async (onboardingData: {
  email: string;
  phoneNumber: string;
  fullName: string;
  clerkUserId?: string;
  planId: string;
  stripePaymentIntentId: string;
  stripeCustomerId?: string; // Added to support direct customer ID passing
  amount: number;
  paymentMethodData?: {
    stripePaymentMethodId: string;
    stripeCustomerId: string;
    cardDetails: {
      last4: string;
      brand: string;
      expMonth: number;
      expYear: number;
      cardholderName: string;
    };
  };
  paymentMethods?: Record<string, any>; // Direct payment methods object for user document
}): Promise<{ 
  success: boolean; 
  user: User; 
  subscription: Subscription; 
  message: string 
}> => {
  console.log('🚀 completeUserOnboarding called with data:', JSON.stringify(onboardingData, null, 2));
  console.log('💳 Payment data check:', {
    hasPaymentMethodData: !!onboardingData.paymentMethodData,
    hasDirectPaymentMethods: !!onboardingData.paymentMethods,
    paymentMethodCount: onboardingData.paymentMethods ? Object.keys(onboardingData.paymentMethods).length : 0
  });
  try {
    console.log('🚀 Starting complete user onboarding for:', onboardingData.email);
    
    const userId = generateUserIdFromEmail(onboardingData.email);
    console.log('📧 Generated userId:', userId);
    
    // Step 1: Create user FIRST (subscription depends on user existing)
    console.log('👤 Creating user...');
    
    // Prepare user data with payment method if available
    const userData = {
      email: onboardingData.email,
      phoneNumber: onboardingData.phoneNumber,
      fullName: onboardingData.fullName,
      clerkUserId: onboardingData.clerkUserId || undefined
    };
    
    // If direct payment methods are provided, use them directly
    if (onboardingData.paymentMethods) {
      console.log('💳 Using directly provided payment methods for user creation');
      (userData as any).paymentMethods = onboardingData.paymentMethods;
      
      // Extract the first payment method to create the cards array
      const firstPaymentMethod = Object.values(onboardingData.paymentMethods)[0];
      if (firstPaymentMethod) {
        (userData as any).cards = [
          {
            brand: firstPaymentMethod.cardDetails?.brand || 'unknown',
            last4: firstPaymentMethod.cardDetails?.last4 || '0000',
            expMonth: firstPaymentMethod.cardDetails?.expMonth || 0,
            expYear: firstPaymentMethod.cardDetails?.expYear || 0,
            stripeCustomerId: firstPaymentMethod.stripeCustomerId,
            stripePaymentMethodId: firstPaymentMethod.stripePaymentMethodId,
            isActive: true,
            cardName: "primaryCard"
          }
        ];
      }
      
      console.log('💳 Direct payment methods prepared for backend:', JSON.stringify((userData as any).paymentMethods, null, 2));
      console.log('💳 Cards array prepared:', JSON.stringify((userData as any).cards, null, 2));
    }
    // Otherwise, if payment method data is available, format it
    else if (onboardingData.paymentMethodData) {
      console.log('💳 Including payment method data in user creation...');
      console.log('💳 Payment method data received:', JSON.stringify(onboardingData.paymentMethodData, null, 2));
      
      // Generate unique payment method ID
      const paymentMethodId = `pm_${onboardingData.email.replace('@', '_at_').replace(/\./g, '_dot_')}_${Date.now()}`;
      
      // Use the top-level customer ID if available, otherwise use the one from payment method data
      const stripeCustomerId = onboardingData.stripeCustomerId || onboardingData.paymentMethodData.stripeCustomerId;
      
      if (onboardingData.stripeCustomerId && onboardingData.stripeCustomerId !== onboardingData.paymentMethodData.stripeCustomerId) {
        console.log('⚠️ Customer ID mismatch - using top-level customer ID:', onboardingData.stripeCustomerId, 
                    'instead of payment method customer ID:', onboardingData.paymentMethodData.stripeCustomerId);
      }
      
      console.log('✅ Using customer ID for payment method:', stripeCustomerId);
      
      // Prepare payment method data following the reference pattern
      const paymentMethodData = {
        paymentMethodId,
        email: onboardingData.email.toLowerCase().trim(),
        phoneNumber: onboardingData.phoneNumber,
        // Store card details (following the reference pattern)
        cardDetails: {
          cardNumber: null, // Don't store full card number for security
          cvv: null, // Don't store CVV for security
          last4: onboardingData.paymentMethodData.cardDetails.last4,
          brand: onboardingData.paymentMethodData.cardDetails.brand,
          expMonth: onboardingData.paymentMethodData.cardDetails.expMonth,
          expYear: onboardingData.paymentMethodData.cardDetails.expYear,
          cardholderName: onboardingData.paymentMethodData.cardDetails.cardholderName
        },
        // Store Stripe IDs for future renewals (following the reference pattern)
        stripePaymentMethodId: onboardingData.paymentMethodData.stripePaymentMethodId,
        stripeCustomerId: stripeCustomerId, // Use the verified customer ID
        isActive: true,
        isDefault: true, // First payment method is default
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Add payment methods to user data
      (userData as any).paymentMethods = {
        [paymentMethodId]: paymentMethodData
      };
      
      // Also add the cards array format
      (userData as any).cards = [
        {
          brand: onboardingData.paymentMethodData.cardDetails.brand || 'unknown',
          last4: onboardingData.paymentMethodData.cardDetails.last4 || '0000',
          expMonth: onboardingData.paymentMethodData.cardDetails.expMonth || 0,
          expYear: onboardingData.paymentMethodData.cardDetails.expYear || 0,
          stripeCustomerId: stripeCustomerId,
          stripePaymentMethodId: onboardingData.paymentMethodData.stripePaymentMethodId,
          isActive: true,
          cardName: "primaryCard"
        }
      ];
      
      console.log('💳 Payment methods prepared for backend:', JSON.stringify((userData as any).paymentMethods, null, 2));
      console.log('💳 Cards array prepared:', JSON.stringify((userData as any).cards, null, 2));
    } else if (onboardingData.stripeCustomerId) {
      console.log('⚠️ No payment method data available, but customer ID is provided:', onboardingData.stripeCustomerId);
      
      // Generate unique payment method ID
      const paymentMethodId = `pm_${onboardingData.email.replace('@', '_at_').replace(/\./g, '_dot_')}_${Date.now()}`;
      
      // Create a minimal payment method entry with just the customer ID
      const paymentMethodData = {
        paymentMethodId,
        email: onboardingData.email.toLowerCase().trim(),
        phoneNumber: onboardingData.phoneNumber,
        stripeCustomerId: onboardingData.stripeCustomerId,
        isActive: true,
        isDefault: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Add payment methods to user data
      (userData as any).paymentMethods = {
        [paymentMethodId]: paymentMethodData
      };
      
      console.log('💳 Created minimal payment method with customer ID:', onboardingData.stripeCustomerId);
    } else {
      console.log('⚠️ No payment method data or customer ID available for user creation');
    }
    
    console.log('📝 About to call createUser with data:', {
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      fullName: userData.fullName,
      hasPaymentMethods: !!(userData as any).paymentMethods,
      paymentMethodCount: (userData as any).paymentMethods ? Object.keys((userData as any).paymentMethods).length : 0
    });
    
    const userResult = await createUser(userData);
    
    if (!userResult.success) {
      throw new Error(`Failed to create user: ${userResult.message}`);
    }
    
    console.log('✅ User created successfully, now creating subscription...');
    
    // Step 2: Create subscription AFTER user is created
    console.log('💳 Creating subscription...');
    const subscriptionResult = await createSubscription({
      email: onboardingData.email,
      phoneNumber: onboardingData.phoneNumber,
      fullName: onboardingData.fullName,
      planId: onboardingData.planId,
      stripePaymentIntentId: onboardingData.stripePaymentIntentId,
      stripeCustomerId: onboardingData.stripeCustomerId, // Pass customer ID to subscription creation
      amount: onboardingData.amount
    });
    
    console.log('✅ Subscription created with customer ID:', onboardingData.stripeCustomerId || 'Not provided');
    
    if (!subscriptionResult.success) {
      throw new Error(`Failed to create subscription: ${subscriptionResult.message}`);
    }
    
    console.log('✅ User onboarding completed successfully');
    
    return {
      success: true,
      user: userResult.user || userResult.data || ({ 
        userId: userResult.id || 'unknown',
        email: onboardingData.email,
        phoneNumber: onboardingData.phoneNumber,
        fullName: onboardingData.fullName
      } as unknown as User),
      subscription: subscriptionResult.subscription,
      message: 'User onboarding completed successfully'
    };
    
  } catch (error: any) {
    console.error('❌ Error completing user onboarding:', error);
    throw error; // Re-throw to let caller handle
  }
};

/**
 * PHASE 1: Validate payment method data without storing it
 * This is used for Phase 1 of the payment flow
 */
export const storePaymentMethodData = async (data: {
  email: string;
  stripeCustomerId: string;
  stripePaymentIntentId: string;
  paymentMethodData: {
    stripePaymentMethodId: string;
    stripeCustomerId: string;
    cardDetails: {
      last4: string;
      brand: string;
      expMonth: number;
      expYear: number;
      cardholderName: string;
    };
  };
}): Promise<{ success: boolean; message: string }> => {
  console.log('💳 [PHASE 1] storePaymentMethodData called with data:', JSON.stringify(data, null, 2));
  
  try {
    // For Phase 1, we just validate and return the data without storing it on the server
    // This is just to verify the data is correctly formatted and available in Redux
    
    // Require a real Stripe customer ID (should start with 'cus_')
    if (!data.stripeCustomerId || !data.stripeCustomerId.startsWith('cus_')) {
      console.error('❌ [PHASE 1] Invalid or missing customer ID');
      throw new Error('Invalid or missing Stripe customer ID');
    }
    
    // Require payment method data
    if (!data.paymentMethodData) {
      console.error('❌ [PHASE 1] No payment method data provided');
      throw new Error('Payment method data is required');
    }
    
    // Require a real Stripe payment method ID (should start with 'pm_')
    if (!data.paymentMethodData.stripePaymentMethodId || !data.paymentMethodData.stripePaymentMethodId.startsWith('pm_')) {
      console.error('❌ [PHASE 1] Invalid or missing payment method ID');
      throw new Error('Invalid or missing Stripe payment method ID');
    }
    
    // Log the data for verification
    console.log('✅ [PHASE 1] Payment method data is valid:', {
      email: data.email,
      stripeCustomerId: data.stripeCustomerId,
      stripePaymentMethodId: data.paymentMethodData.stripePaymentMethodId,
      cardBrand: data.paymentMethodData.cardDetails.brand,
      cardLast4: data.paymentMethodData.cardDetails.last4
    });
    
    return {
      success: true,
      message: 'Payment method data validated successfully'
    };
  } catch (error: any) {
    console.error('❌ [PHASE 1] Error validating payment method data:', error);
    throw error;
  }
};

/**
 * PHASE 2: Store payment method data directly in the user document
 * This is used for Phase 2 of the payment flow
 */
export const storePaymentMethodInUser = async (data: {
  email: string;
  stripeCustomerId: string;
  stripePaymentMethodId: string;
  cardDetails: {
    last4: string;
    brand: string;
    expMonth: number;
    expYear: number;
    cardholderName: string;
  };
  phoneNumber?: string;
}): Promise<{ success: boolean; paymentMethodId?: string; message: string }> => {
  console.log('💳 [PHASE 2] storePaymentMethodInUser called with data:', JSON.stringify(data, null, 2));
  
  try {
    // Generate userId from email to use in the API endpoint
    const userId = generateUserIdFromEmail(data.email);
    
    // Generate unique payment method ID
    const paymentMethodId = `pm_${data.email.replace('@', '_at_').replace(/\./g, '_dot_')}_${Date.now()}`;
    
    // Prepare payment method data
    const paymentMethod = {
      cardDetails: {
        cardNumber: null, // Don't store full card number for security
        cvv: null, // Don't store CVV for security
        last4: data.cardDetails.last4,
        brand: data.cardDetails.brand,
        expMonth: data.cardDetails.expMonth,
        expYear: data.cardDetails.expYear,
        cardholderName: data.cardDetails.cardholderName
      },
      createdAt: new Date().toISOString(),
      email: data.email,
      isActive: true,
      isDefault: true,
      paymentMethodId: paymentMethodId,
      phoneNumber: data.phoneNumber || null,
      stripeCustomerId: data.stripeCustomerId,
      stripePaymentMethodId: data.stripePaymentMethodId,
      updatedAt: new Date().toISOString()
    };
    
    // Use the legacy endpoint to update user profile with payment method
    const response = await apiClient.put(`/user/user-profile/${userId}`, {
      stripeCustomerId: data.stripeCustomerId,
      // Include the full paymentMethods object for reference
      paymentMethods: {
        [paymentMethodId]: paymentMethod
      },
      // Add the new cards array structure
      cards: [
        {
          brand: data.cardDetails.brand || 'unknown',
          last4: data.cardDetails.last4 || '0000',
          expMonth: data.cardDetails.expMonth || 0,
          expYear: data.cardDetails.expYear || 0,
          stripeCustomerId: data.stripeCustomerId,
          stripePaymentMethodId: data.stripePaymentMethodId,
          isActive: true,
          cardName: "primaryCard"
        }
      ]
    });
    
    console.log('✅ [PHASE 2] Payment method stored in user document:', response.data);
    
    return {
      success: true,
      paymentMethodId: paymentMethodId,
      message: 'Payment method stored successfully'
    };
  } catch (error: any) {
    console.error('❌ [PHASE 2] Error storing payment method in user document:', error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 404) {
      throw new Error('User not found - the user must be created before storing a payment method');
    } else if (error.response?.status) {
      throw new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      throw new Error('Network error: Cannot reach API. Please check your connection.');
    } else {
      throw new Error('Failed to store payment method. Please try again.');
    }
  }
};

// Keep the old function for backward compatibility but make it use the new approach
export const storePaymentMethodSeparately = storePaymentMethodInUser;

// Export legacy API functions for backward compatibility during migration
export const legacyCreateUserProfile = createUser;
export const legacyAddPaidUserData = createSubscription;
export const legacyGetUserProfileData = getUserWithSubscription;